"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { generateImageURL } from "@/lib/utils";
import { ScrollRow } from "../ScrollRow";
import type { ProjectProperty } from "./types";

/** Four photos per row, side-scroll with a progress bar; click opens full screen. */
export function ProjectPhotoStrip({ property }: { property: ProjectProperty }) {
  const files = property.images?.property_images ?? [];
  const images = files.map((file) => generateImageURL(property.name, "property_images", file));
  const [active, setActive] = useState<number | null>(null);
  const label = property.title || property.name;

  useEffect(() => {
    if (active == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => (i == null ? 0 : (i + 1) % images.length));
      if (e.key === "ArrowLeft") setActive((i) => (i == null ? 0 : (i - 1 + images.length) % images.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, images.length]);

  if (images.length === 0) return null;

  return (
    <>
      <ScrollRow>
        {images.map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`Open photo ${i + 1} of ${images.length}`}
            className="relative aspect-[4/3] w-[78%] shrink-0 snap-start overflow-hidden rounded-2xl bg-surface sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-3rem)/4)]"
          >
            <Image src={src} alt={`${label} photo ${i + 1}`} fill unoptimized className="object-cover transition-transform duration-300 hover:scale-[1.03]" sizes="(min-width: 1024px) 25vw, 50vw" />
          </button>
        ))}
      </ScrollRow>

      {active != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4 md:p-8" role="dialog" aria-modal="true" aria-label={`${label} photos`} onMouseDown={(e) => { if (e.target === e.currentTarget) setActive(null); }}>
          <div className="relative h-[85vh] w-full max-w-7xl">
            <Image src={images[active]} alt={`${label} photo ${active + 1}`} fill unoptimized className="object-contain" sizes="100vw" />
            <button type="button" onClick={() => setActive((i) => ((i ?? 0) - 1 + images.length) % images.length)} aria-label="Previous photo" className="absolute left-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg">
              <ChevronLeft aria-hidden="true" className="size-5" />
            </button>
            <button type="button" onClick={() => setActive((i) => ((i ?? 0) + 1) % images.length)} aria-label="Next photo" className="absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg">
              <ChevronRight aria-hidden="true" className="size-5" />
            </button>
            <button type="button" onClick={() => setActive(null)} aria-label="Close" className="absolute right-0 top-0 rounded-full bg-white p-3 text-ink shadow-lg">
              <X aria-hidden="true" className="size-5" />
            </button>
            <p className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-ink">{active + 1} of {images.length}</p>
          </div>
        </div>
      )}
    </>
  );
}
